import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, isWorkspaceMismatch } from "@/lib/api";
import PlatformIcon from "@/components/PlatformIcon";
import { useEntitlements } from "@/lib/useEntitlements";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, Trash2, ExternalLink, Search, Inbox, ChevronLeft, ChevronRight, Lock, ArrowRight } from "lucide-react";
import { funnelStageLabel, funnelStageBadgeClass } from "@/lib/funnelStage";

const COLUMNS = [
  { id: "new", label: "New", head: "bg-amber-100 text-amber-800", badge: "bg-amber-500" },
  { id: "contacted", label: "Contacted", head: "bg-blue-100 text-blue-800", badge: "bg-blue-500" },
  { id: "replied", label: "Replied", head: "bg-purple-100 text-purple-800", badge: "bg-purple-500" },
  { id: "closed", label: "Closed", head: "bg-emerald-100 text-emerald-800", badge: "bg-emerald-500" },
];

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24); if (days < 30) return `${days}d ago`;
  const mo = Math.floor(days / 30); if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

export default function SavedLeads() {
  const { loaded: entLoaded, unknown: entUnknown, isPaid } = useEntitlements();
  // Phase 5.3 — leads are workspace-scoped, and this list used to be fetched
  // once per mount. That was only safe while a workspace switch reloaded the
  // page; without the epoch, switching leaves the previous tenant's saved leads
  // on screen, draggable, deletable.
  // B3 — now the TENANT epoch, so a brand change re-runs this too. `load` also
  // re-checks the epoch after its await: this component stays mounted through a
  // brand switch, so there is no unmount and no cancel flag that would fire.
  // B4 — this list is now BRAND-scoped. It fetched `/leads` with no `brand_id`,
  // so in a multi-brand workspace every brand's saved leads blended into one
  // board: you opened "Saved Leads" while working on client B and were handed
  // A's and B's leads mixed together, with nothing on the row to say which was
  // which. The endpoint always accepted the filter; nothing passed it. It also
  // re-runs on a brand change now, which `tenantEpoch` already covers.
  const { tenantEpoch, brand, brandLoading, reportBrandGone } = useAuth();
  const epochRef = useRef(tenantEpoch);
  epochRef.current = tenantEpoch;
  const brandId = brand?.brand_id || null;
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [dragOver, setDragOver] = useState(null);
  const [dragId, setDragId] = useState(null);

  const load = useCallback(async () => {
    const epoch = epochRef.current;
    const stale = () => epochRef.current !== epoch;
    // Fail closed. With no brand seated there is no honest list to show, and
    // the unscoped fetch that used to happen here is exactly the bug: it
    // returned every brand's leads and called them this screen's.
    if (!brandId) {
      setLeads([]);
      setLoading(brandLoading);
      return;
    }
    setLoading(true);
    setError(null);
    // "Stay in the loading state and say nothing" — set by every path where the
    // right answer is neither data nor an error message: a mismatch that is
    // mid-repair, or a response that belongs to a tenant we have left. Both end
    // with another `load()` already on its way.
    let holdLoading = false;
    try {
      const r = await api.get("/leads", { params: { brand_id: brandId } });
      // AFTER the await. A response fetched for the previous tenant must not
      // land, and the effect below has already cleared the list for the new one.
      if (stale()) { holdLoading = true; return; }
      setLeads(r.data?.items || []);
    } catch (e) {
      if (stale()) { holdLoading = true; return; }
      // B6 — a 404 means this brand is gone, not that leads failed to load.
      // Same argument as the workspace mismatch below: a re-derive is already
      // on its way, so stay quiet here and let the banner do the talking.
      if (e?.response?.status === 404) {
        holdLoading = true;
        reportBrandGone?.(brandId);
        return;
      }
      // A workspace mismatch is NOT a failure to report. The interceptor is
      // already re-syncing, the epoch will bump, and this effect will re-run
      // against the right tenant. "Could not load saved leads" would be a false
      // statement about a problem that is mid-repair — so stay in the loading
      // state and say nothing.
      if (isWorkspaceMismatch(e)) { holdLoading = true; return; }
      // Third raw-`detail` leak (the spec named Dashboard.jsx and
      // WorkspaceSwitcher.jsx; this one is the same bug). Server text never
      // reaches a screen — console gets the truth, the user gets our words.
      // eslint-disable-next-line no-console
      console.error("[saved-leads] load failed", e?.response?.status, e?.response?.data?.detail);
      setError("Could not load saved leads.");
    } finally {
      if (!holdLoading) setLoading(false);
    }
  }, [brandId, brandLoading, reportBrandGone]);

  useEffect(() => {
    setLeads([]); // never show the previous tenant's leads while the next load runs
    setError(null);
    load();
  }, [load, tenantEpoch]);

  // Both mutations below capture the epoch. A `lead_id` belongs to the tenant
  // whose list it came from, so once the tenant moves, neither the confirmation
  // toast nor the error-path `load()` is about anything on screen — and an
  // "Undo" offered against a lead in a workspace the user has left would act on
  // a tenant they are no longer looking at.
  const move = async (leadId, status) => {
    const prev = leads.find((l) => l.lead_id === leadId);
    if (!prev || prev.status === status) return;
    const epoch = epochRef.current;
    setLeads((ls) => ls.map((l) => (l.lead_id === leadId ? { ...l, status } : l)));
    try {
      await api.patch(`/leads/${leadId}`, { status });
      if (epochRef.current !== epoch) return;
      toast.success(`Moved to ${COLUMNS.find((c) => c.id === status)?.label}`, {
        action: {
          label: "Undo",
          onClick: () => {
            if (epochRef.current !== epoch) return;
            move(leadId, prev.status || "new");
          },
        },
      });
    } catch (e) {
      if (epochRef.current !== epoch) return;
      toast.error("Could not move lead.");
      load();
    }
  };

  const remove = async (lead) => {
    if (!window.confirm("Remove this lead from your board?")) return;
    const epoch = epochRef.current;
    setLeads((ls) => ls.filter((l) => l.lead_id !== lead.lead_id));
    try {
      await api.delete(`/leads/${lead.lead_id}`);
      if (epochRef.current !== epoch) return;
      toast.success("Lead removed.");
    } catch (e) {
      if (epochRef.current !== epoch) return;
      toast.error("Could not remove lead.");
      load();
    }
  };

  const shift = (lead, dir) => {
    const idx = COLUMNS.findIndex((c) => c.id === (lead.status || "new"));
    const next = COLUMNS[idx + dir];
    if (next) move(lead.lead_id, next.id);
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter((l) => {
      const r = l.result || {};
      return (r.author || "").toLowerCase().includes(q) || (r.content || "").toLowerCase().includes(q) || (r.title || "").toLowerCase().includes(q);
    });
  }, [leads, query]);

  const grouped = useMemo(() => {
    const g = { new: [], contacted: [], replied: [], closed: [] };
    for (const l of filtered) (g[l.status || "new"] || g.new).push(l);
    return g;
  }, [filtered]);

  const onCardKey = (e, lead) => {
    if (e.key === "ArrowRight") { e.preventDefault(); shift(lead, 1); }
    else if (e.key === "ArrowLeft") { e.preventDefault(); shift(lead, -1); }
    else if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); remove(lead); }
  };

  // Wait for BOTH the leads and the plan. Rendering the board early would mean
  // picking between the kanban and the upgrade wall on a coin toss, and the
  // wrong guess shows a paying customer an ad for what they already bought.
  // "Loading your board…" commits to neither.
  // §4: NO full-page loading strand. This used to own the whole viewport with a
  // centred spinner and "Loading your board…" — forever, if the workspace fetch
  // failed. Under `unknown` we show the app's SKELETON, not a void: the real
  // header and column structure with empty content areas, so the page still
  // reads as the product rather than as a dead screen. And it says nothing
  // about the error — the one banner above owns that (§4).
  if (!entLoaded && entUnknown) {
    return (
      <div className="pb-6" data-testid="saved-leads-unknown">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight text-lm-text">
            Saved Leads
          </h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-xl border border-lm-border bg-lm-surface/60 p-3 min-h-[180px]">
              <div className="h-3 w-24 rounded bg-lm-tint mb-3" />
              <div className="h-16 rounded-lg bg-lm-tint/60" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Wait for BOTH the leads and the plan while either is genuinely in flight.
  if (loading || !entLoaded) {
    return (
      <div
        role="status"
        data-testid={loading ? "saved-leads-loading" : "saved-leads-pending"}
        className="flex items-center gap-2 text-lm-text2"
      >
        <Loader2 className="w-4 h-4 animate-spin motion-reduce:animate-none" /> Loading your board…
      </div>
    );
  }
  if (error) {
    return <div className="rounded-xl border border-red-200 bg-red-50/60 p-4 text-sm text-red-700">{error}</div>;
  }

  return (
    <div className="pb-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-baseline gap-3">
          <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight">Saved leads</h1>
          <span className="text-sm text-lm-text3 font-mono">{leads.length} total</span>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 text-lm-text3 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search leads…"
            className="w-56 md:w-72 pl-9 pr-3 py-2 rounded-lg border border-lm-border bg-lm-surface text-sm focus:outline-none focus:ring-2 focus:ring-lm-primary/20 focus:border-lm-primary"
          />
        </div>
      </div>

      {!isPaid ? (
        <ProLockedBoard />
      ) : leads.length === 0 ? (
        <EmptyBoard />
      ) : (
        <>
          <p className="text-xs text-lm-text3 mb-4">
            Drag cards between columns, or focus a card and use <Kbd>←</Kbd> <Kbd>→</Kbd> to move it, <Kbd>Del</Kbd> to remove.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pb-4">
            {COLUMNS.map((col) => {
              const items = grouped[col.id] || [];
              const isOver = dragOver === col.id;
              return (
                <div
                  key={col.id}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(col.id); }}
                  onDragLeave={() => setDragOver((c) => (c === col.id ? null : c))}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(null);
                    const id = e.dataTransfer.getData("text/plain") || dragId;
                    if (id) move(id, col.id);
                  }}
                  className={`flex flex-col min-w-0 rounded-xl p-1 transition-colors ${isOver ? "bg-lm-tint/60" : ""}`}
                >
                  {/* Column header */}
                  <div className={`flex items-center justify-between rounded-lg px-4 py-2.5 mb-3 ${col.head}`}>
                    <span className="text-sm font-semibold uppercase tracking-wide">{col.label}</span>
                    <span className="text-xs font-bold bg-white/70 rounded-md px-2 py-0.5 tabular-nums">{items.length}</span>
                  </div>

                  {/* Cards */}
                  <div className="flex flex-col gap-3 min-h-[80px]">
                    {items.length === 0 && (
                      <div className="text-xs text-lm-text3 italic px-1 py-4 text-center border-2 border-dashed border-lm-border rounded-xl">
                        Drop leads here
                      </div>
                    )}
                    {items.map((lead) => (
                      <LeadKanbanCard
                        key={lead.lead_id}
                        lead={lead}
                        onDragStart={(e) => { e.dataTransfer.setData("text/plain", lead.lead_id); e.dataTransfer.effectAllowed = "move"; setDragId(lead.lead_id); }}
                        onDragEnd={() => setDragId(null)}
                        onKeyDown={(e) => onCardKey(e, lead)}
                        onShift={(dir) => shift(lead, dir)}
                        onRemove={() => remove(lead)}
                        colIndex={COLUMNS.findIndex((c) => c.id === col.id)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function LeadKanbanCard({ lead, onDragStart, onDragEnd, onKeyDown, onShift, onRemove, colIndex }) {
  const r = lead.result || {};
  const stage = r.funnel_stage;
  const score = Math.round(r.opportunity_score || 0);
  const content = (r.content || "").trim();
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onKeyDown={onKeyDown}
      tabIndex={0}
      className="group bg-lm-surface border border-lm-border rounded-xl p-3.5 shadow-sm hover:shadow-md focus:ring-2 focus:ring-lm-primary/30 focus:outline-none transition-all cursor-grab active:cursor-grabbing"
    >
      <div className="flex items-start gap-2.5">
        <div className="w-9 h-9 rounded-full bg-lm-tint2 flex items-center justify-center shrink-0">
          <PlatformIcon platform={r.platform} className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-lm-text truncate" title={r.author}>{r.author || "unknown"}</div>
          <div className="text-[11px] text-lm-text3 font-mono">{timeAgo(r.posted_at || lead.created_at)}</div>
        </div>
        <div className="text-xs font-mono font-bold text-lm-primary bg-lm-tint2 rounded px-1.5 py-0.5 shrink-0">{score}</div>
      </div>

      {content && (
        <p className="text-xs text-lm-text2 leading-snug mt-2.5 line-clamp-3">{content}</p>
      )}

      <div className="flex items-center gap-2 mt-3 pt-2.5 border-t border-lm-border">
        {stage && (
          <span className={`px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-wider font-semibold ${funnelStageBadgeClass(stage)}`}>
            {funnelStageLabel(stage)}
          </span>
        )}
        {r.url && (
          <a href={r.url} target="_blank" rel="noreferrer" className="text-lm-text3 hover:text-lm-primary" title="Open" onClick={(e) => e.stopPropagation()}>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
        <div className="ml-auto flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          <button onClick={() => onShift(-1)} disabled={colIndex === 0} className="p-1 rounded text-lm-text3 hover:text-lm-primary hover:bg-lm-tint disabled:opacity-30" title="Move left" aria-label="Move left">
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onShift(1)} disabled={colIndex === COLUMNS.length - 1} className="p-1 rounded text-lm-text3 hover:text-lm-primary hover:bg-lm-tint disabled:opacity-30" title="Move right" aria-label="Move right">
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
          <button onClick={onRemove} className="p-1 rounded text-lm-text3 hover:text-red-600 hover:bg-red-50" title="Remove" aria-label="Remove">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function Kbd({ children }) {
  return <kbd className="inline-flex items-center px-1.5 py-0.5 rounded border border-lm-border bg-lm-surface text-[10px] font-mono text-lm-text2 mx-0.5">{children}</kbd>;
}

function EmptyBoard() {
  return (
    <div className="rounded-2xl border-2 border-dashed border-lm-border bg-lm-surface p-14 text-center">
      <div className="w-14 h-14 rounded-2xl bg-lm-tint2 flex items-center justify-center mx-auto mb-5">
        <Inbox className="w-6 h-6 text-lm-primary" />
      </div>
      <h3 className="text-xl font-display font-semibold mb-2">No saved leads yet.</h3>
      <p className="text-lm-text2 max-w-md mx-auto">
        Save leads from a Find Customers scan and they'll show up here as a board you can drag through New → Contacted → Replied → Closed.
      </p>
    </div>
  );
}

// Free users don't get the kanban board — show an upgrade prompt instead.
function ProLockedBoard() {
  const navigate = useNavigate();
  return (
    <div className="rounded-2xl border-2 border-dashed border-lm-border bg-lm-surface p-14 text-center">
      <div className="w-14 h-14 rounded-2xl bg-lm-tint2 flex items-center justify-center mx-auto mb-5">
        <Lock className="w-6 h-6 text-lm-primary" />
      </div>
      <h3 className="text-xl font-display font-semibold mb-2">Saving leads is a Pro feature.</h3>
      <p className="text-lm-text2 max-w-md mx-auto mb-6">
        To save your best leads to a kanban board — and drag them through
        New → Contacted → Replied → Closed — you'll need the Starter plan.
        Upgrade to start building your pipeline.
      </p>
      <button
        onClick={() => navigate("/app/billing")}
        className="inline-flex items-center gap-2 bg-lm-primary text-white rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-lm-primaryHover"
      >
        Upgrade to save leads <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}
